import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useSoulProfile } from "@/lib/useSoulProfile";
import { houseFor } from "@/lib/houses";
import { HANDDRAWN } from "@/constants/testIds";
import { playAudioLogo } from "@/lib/audioLogo";
import { useNarrator } from "@/lib/narrator";
import { useIsMobile } from "@/lib/useIsMobile";
import NarratedText from "@/components/NarratedText";
import FanWheel from "@/components/FanWheel";
import CardFace from "@/components/CardFace";

/**
 * Hand Drawn — the tactile, spread-first reading.
 *
 * Flow:
 *   1. Pick a spread (existing spread catalogue, excluding Card of the Day)
 *   2. Draw stage — a fan of card backs (one per position). Click any back
 *      to open the Fan Wheel for that position.
 *   3. Fan Wheel — 5 sections × traditional 78 tarot; tap → bind card;
 *      each bind plays the 528Hz Solfeggio chime.
 *   4. Read the spread — sends chosen cards to /api/oracle/spread/hand-drawn
 *      for the full Claude interpretation.
 *   5. Reading stage — per-position + Thread with manual Hopkins/Burrows
 *      narration (Play/Pause pill per block).
 */
export default function HandDrawn() {
  const { element } = useSoulProfile();
  const house = houseFor(element);
  const { muted } = useNarrator();

  const [spreads, setSpreads] = useState([]);
  const [sections, setSections] = useState([]);
  const [stage, setStage] = useState("picking"); // picking | drawing | reading
  const [spread, setSpread] = useState(null);
  const [assignments, setAssignments] = useState([]); // [{title, upright} | null]
  const [wheelIdx, setWheelIdx] = useState(-1); // which fan position is being drawn
  const [reading, setReading] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/oracle/spreads")
      .then((r) => setSpreads((r.data.spreads || []).filter((s) => s.id !== "card-of-day")))
      .catch(() => {});
    api
      .get("/oracle/tarot-catalog")
      .then((r) => setSections(r.data.sections || []))
      .catch(() => {});
  }, []);

  const pickSpread = (s) => {
    setSpread(s);
    setAssignments(Array(s.positions).fill(null));
    setStage("drawing");
  };

  const openWheel = (i) => setWheelIdx(i);

  const bindCard = ({ title, upright }) => {
    if (wheelIdx < 0) return;
    setAssignments((prev) => {
      const next = [...prev];
      next[wheelIdx] = { title, upright };
      return next;
    });
    playAudioLogo({ muted });
    setWheelIdx(-1);
  };

  const clearAssignment = (i) => {
    setAssignments((prev) => {
      const next = [...prev];
      next[i] = null;
      return next;
    });
  };

  const allFilled = spread && assignments.every((a) => a);

  const submit = async () => {
    if (!allFilled) return;
    setBusy(true);
    try {
      const { data } = await api.post("/oracle/spread/hand-drawn", {
        spread_id: spread.id,
        cards: assignments.map((a) => ({ title: a.title, upright: a.upright })),
      });
      setReading(data);
      setStage("reading");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The spread refused to be read.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSpread(null);
    setAssignments([]);
    setReading(null);
    setStage("picking");
  };

  return (
    <div
      data-testid={HANDDRAWN.page}
      className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-6 sm:py-12 lg:py-20 pb-32 lg:pb-20"
    >
      <div className="starfield-subtle" />
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 sm:mb-14">
          <div className="font-overline text-[#C8A96E] mb-3 sm:mb-4">Hand Drawn</div>
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl text-[#F4EFE6] font-light leading-tight">
            {stage === "picking" && (
              <>
                Choose the shape
                <br />
                of the reading.
              </>
            )}
            {stage === "drawing" && (
              <>
                Draw the cards
                <br />
                your hand chooses.
              </>
            )}
            {stage === "reading" && <>The spread has spoken.</>}
          </h1>
        </div>

        {stage === "picking" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {spreads.map((s) => (
              <button
                key={s.id}
                data-testid={HANDDRAWN.spreadTile(s.id)}
                onClick={() => pickSpread(s)}
                className="text-left card-frame surface-grain p-6 relative flex flex-col justify-between min-h-[190px] transition-all hover:border-[#C8A96E]/50"
              >
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="font-heading italic text-2xl text-[#F4EFE6]">{s.name}</div>
                    {s.premium && (
                      <div className="font-overline text-[#C8A96E] flex items-center gap-1">
                        <Sparkles size={12} strokeWidth={1.5} />
                        Premium
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-[#F4EFE6]/60 leading-relaxed">{s.description}</p>
                </div>
                <div className="mt-6 font-overline text-[#F4EFE6]/40">
                  {s.positions} {s.positions === 1 ? "position" : "positions"}
                </div>
              </button>
            ))}
          </div>
        )}

        {stage === "drawing" && spread && (
          <div>
            <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="font-overline text-[#F4EFE6]/50 mb-2">Spread</div>
                <div className="font-heading italic text-2xl text-[#F4EFE6]">{spread.name}</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid={HANDDRAWN.resetBtn}
                  onClick={reset}
                  className="font-overline text-[#F4EFE6]/60 hover:text-[#C8A96E] transition-colors inline-flex items-center gap-2"
                >
                  <RotateCcw size={14} strokeWidth={1.5} />
                  Change spread
                </button>
              </div>
            </div>

            <FanSpread
              positions={spread.position_names}
              assignments={assignments}
              house={house}
              onDraw={openWheel}
              onClear={clearAssignment}
            />

            <div className="mt-12 flex items-center justify-center">
              <button
                data-testid={HANDDRAWN.readSpreadBtn}
                onClick={submit}
                disabled={!allFilled || busy}
                className="btn-sacred btn-sacred-solid disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? "Interpreting…" : allFilled ? "Read the spread" : `Draw ${assignments.filter((a) => !a).length} more`}
              </button>
            </div>
          </div>
        )}

        {stage === "reading" && reading && (
          <HandDrawnReading reading={reading} onReset={reset} muted={muted} />
        )}
      </div>

      <AnimatePresence>
        {wheelIdx >= 0 && sections.length > 0 && spread && (
          <FanWheel
            sections={sections}
            element={element}
            position={spread.position_names[wheelIdx]}
            onCancel={() => setWheelIdx(-1)}
            onConfirm={bindCard}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Fan of card backs ────────────────────────────────────────────────
function FanSpread({ positions, assignments, house, onDraw, onClear }) {
  const n = positions.length;
  const isMobile = useIsMobile();
  // Compute an arc: cards laid out along a fan from -angleMax to +angleMax.
  const angleMax = isMobile
    ? n <= 3 ? 18 : n <= 5 ? 24 : n <= 7 ? 32 : 44
    : n <= 3 ? 20 : n <= 5 ? 30 : n <= 7 ? 40 : 52;
  const angleStep = n > 1 ? (angleMax * 2) / (n - 1) : 0;
  // Horizontal offset so each card owns a distinct click zone.
  // Tighter on mobile so cards never leave the container edges.
  const spread = isMobile
    ? n <= 3 ? 18 : n <= 5 ? 14 : n <= 7 ? 11 : 8
    : n <= 3 ? 26 : n <= 5 ? 20 : n <= 7 ? 15 : 10;
  const xStep = n > 1 ? (spread * 2) / (n - 1) : 0;

  return (
    <div className="relative w-full flex items-center justify-center py-4 sm:py-6">
      <div
        className="relative"
        style={{
          width: "min(94vw, 1000px)",
          height: "clamp(260px, 38vh, 460px)",
        }}
      >
        {positions.map((pos, i) => {
          const angle = n === 1 ? 0 : -angleMax + i * angleStep;
          const xPct = n === 1 ? 0 : -spread + i * xStep;
          const bound = assignments[i];
          return (
            <FanCardSlot
              key={i}
              index={i}
              angle={angle}
              xPct={xPct}
              zIndex={i}
              position={pos}
              bound={bound}
              house={house}
              onClick={() => (bound ? onClear(i) : onDraw(i))}
            />
          );
        })}
      </div>
    </div>
  );
}

function FanCardSlot({ index, angle, xPct, zIndex, position, bound, house, onClick }) {
  return (
    <motion.button
      type="button"
      data-testid={HANDDRAWN.fanCard(index)}
      onClick={onClick}
      initial={{ opacity: 0, y: 30, rotate: 0 }}
      animate={{ opacity: 1, y: 0, rotate: angle }}
      transition={{ duration: 1.1, delay: index * 0.12, ease: [0.25, 0.6, 0.3, 1] }}
      whileHover={{ y: -18, transition: { duration: 0.5 } }}
      className="absolute bottom-0 rounded-2xl overflow-hidden focus:outline-none cursor-pointer"
      style={{
        left: `calc(50% + ${xPct}%)`,
        width: "clamp(96px, 26vw, 180px)",
        aspectRatio: "3 / 4",
        transform: `translateX(-50%) rotate(${angle}deg)`,
        transformOrigin: "50% 130%",
        zIndex,
        border: bound ? "2px solid #D4AF37" : "1px solid rgba(212,175,55,0.25)",
        boxShadow: bound
          ? "0 0 34px rgba(212,175,55,0.35), 0 22px 60px rgba(0,0,0,0.55)"
          : "0 22px 60px rgba(0,0,0,0.55)",
        background: bound ? "#080808" : "transparent",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
      }}
    >
      {bound ? (
        <>
          <CardFace
            title={bound.title}
            upright={bound.upright}
            size={180}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: 0,
              border: "none",
              boxShadow: "none",
            }}
          />
          {/* Position label anchored to the top so it always reads upright */}
          <div
            className="absolute top-1.5 inset-x-0 text-center pointer-events-none font-overline text-[9px] tracking-[0.22em]"
            style={{ color: "#C8A96E" }}
          >
            {position} · {bound.upright ? "Upright" : "Reversed"}
          </div>
        </>
      ) : (
        <>
          <img
            src={house.back}
            alt={`${house.name} House card back`}
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            draggable={false}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.85) 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 px-2 pb-3 text-center pointer-events-none">
            <div className="font-overline text-[9px] tracking-[0.22em] text-[#C8A96E] mb-1">
              {position}
            </div>
            <div className="font-overline text-[10px] tracking-[0.22em] text-[#F4EFE6]/50">
              Tap to draw
            </div>
          </div>
        </>
      )}
    </motion.button>
  );
}

// ─── Reading result ────────────────────────────────────────────────────
function HandDrawnReading({ reading, onReset, muted }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-overline text-[#F4EFE6]/50 mb-2">Spread · Hand drawn</div>
          <div className="font-heading italic text-2xl text-[#F4EFE6]">{reading.spread_name}</div>
        </div>
        <button
          onClick={onReset}
          className="font-overline text-[#F4EFE6]/60 hover:text-[#C8A96E] transition-colors inline-flex items-center gap-2"
        >
          <RotateCcw size={14} strokeWidth={1.5} />
          Draw a new spread
        </button>
      </div>

      {/* Cards summary */}
      <div
        className="grid gap-4 mb-12"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(160px, 1fr))`,
        }}
      >
        {reading.cards.map((c, i) => (
          <div
            key={i}
            className="card-frame p-4 flex flex-col justify-between min-h-[110px]"
          >
            <div className="font-overline text-[#C8A96E]">{c.position}</div>
            <div className="mt-2">
              <div className="font-heading italic text-lg text-[#F4EFE6] leading-tight">
                {c.title}
              </div>
              <div className="font-overline text-[10px] text-[#F4EFE6]/50 mt-1">
                {c.upright ? "Upright" : "Reversed"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Positions */}
      <div className="space-y-8 mb-12">
        {reading.positions.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: i * 0.3 }}
            className="pl-6 border-l-2 border-[#C8A96E]/25"
          >
            <div className="font-overline text-[#C8A96E] mb-2">{p.position}</div>
            <NarratedText
              id={`handdrawn-position-${i}`}
              text={p.reading}
              as="p"
              className="font-heading text-xl text-[#F4EFE6]/90 leading-relaxed"
              manual
            />
          </motion.div>
        ))}
      </div>

      {reading.thread && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, delay: 0.6 + reading.positions.length * 0.3 }}
          className="card-frame surface-grain p-8"
        >
          <div className="font-overline text-[#C8A96E] mb-3">The Thread</div>
          <NarratedText
            id="handdrawn-thread"
            text={reading.thread}
            as="p"
            className="font-heading text-xl sm:text-2xl italic text-[#F4EFE6]/95 leading-relaxed"
            manual
          />
        </motion.div>
      )}
    </div>
  );
}
